import { collection, doc, getDoc, getDocs, orderBy, query, Timestamp, where } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "./config";

const functions = getFunctions(undefined, "europe-central2");
const manageOrganization = httpsCallable<Record<string, unknown>, any>(
  functions,
  "manageOrganizationFoundation"
);

export type OrganizationType =
  | "NATIONAL_FEDERATION"
  | "REGIONAL_FEDERATION"
  | "CLUB"
  | "NATIONAL_TEAM"
  | "REGIONAL_TEAM"
  | "EVENT_DELEGATION"
  | "FOREIGN_CLUB"
  | "GUEST_TEAM";

export interface Organization {
  id: string;
  officialName: string;
  shortName?: string;
  type: OrganizationType;
  countryCode: string;
  countryName?: string;
  parentOrganizationId?: string | null;
  regionCode?: string | null;
  external: boolean;
  status: "ACTIVE" | "INACTIVE";
  legacyClubId?: string;
  legacyClubName?: string;
  slug?: string;
  location?: string;
  publicDescription?: string;
  imageUrl?: string;
}

export interface CurrentAthleteMembership {
  id: string;
  athleteId: string;
  athleteDocId: string;
  membershipId: string;
  organizationId: string;
  organizationName: string;
  status: "ACTIVE";
  updatedAt?: Timestamp;
}

export interface MembershipSuggestion {
  athleteId: string;
  suggestedClub: string;
  occurrenceCount: number;
  alternatives: string[];
}

export interface PermissionGrant {
  id: string;
  userId: string;
  organizationId: string;
  permission: "organization.manage_roster" | "event.register" | "event.register_regional_delegation";
  status: "ACTIVE" | "REVOKED";
  validFrom?: Timestamp;
  validUntil?: Timestamp | null;
}

export interface CoachOrganizationAccess {
  userId: string;
  rosterOrganizationIds: string[];
  registrationOrganizationIds: string[];
  regionalRegistrationOrganizationIds: string[];
  updatedAt?: Timestamp;
}

export interface ClubTransferRequest {
  id: string;
  athleteId: string;
  fromOrganizationId: string;
  fromOrganizationName: string;
  toOrganizationId: string;
  toOrganizationName: string;
  status: "REQUESTED" | "APPROVED" | "REJECTED";
  reason?: string;
  reviewReason?: string;
  createdAt?: Timestamp;
}

export interface AthleteMembershipHistory {
  id: string;
  athleteId: string;
  athleteNameSnapshot: string;
  organizationId: string;
  organizationNameSnapshot: string;
  status: "ACTIVE" | "ENDED";
  validFrom?: Timestamp;
  validUntil?: Timestamp | null;
  source?: string;
}

export async function getOrganizations(): Promise<Organization[]> {
  const snap = await getDocs(query(collection(db, "organizations"), orderBy("officialName")));
  return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() } as Organization));
}

export async function getCurrentAthleteMemberships(organizationIds?: string[]): Promise<CurrentAthleteMembership[]> {
  if (!organizationIds) {
    const snap = await getDocs(collection(db, "currentAthleteMemberships"));
    return snap.docs.map((row) => ({ id: row.id, ...row.data() } as CurrentAthleteMembership));
  }
  const uniqueIds = [...new Set(organizationIds.filter(Boolean))];
  if (!uniqueIds.length) return [];
  const rows: CurrentAthleteMembership[] = [];
  for (let index = 0; index < uniqueIds.length; index += 30) {
    const snap = await getDocs(query(
      collection(db, "currentAthleteMemberships"),
      where("organizationId", "in", uniqueIds.slice(index, index + 30))
    ));
    rows.push(...snap.docs.map((row) => ({ id: row.id, ...row.data() } as CurrentAthleteMembership)));
  }
  return rows;
}

export async function getPermissionGrants(userId?: string): Promise<PermissionGrant[]> {
  const source = userId
    ? query(collection(db, "permissionGrants"), where("userId", "==", userId))
    : collection(db, "permissionGrants");
  const snap = await getDocs(source);
  return snap.docs.map((row) => ({ id: row.id, ...row.data() } as PermissionGrant));
}

export async function getCoachOrganizationAccess(userId: string): Promise<CoachOrganizationAccess> {
  const snapshot = await getDoc(doc(db, "coachOrganizationAccess", userId));
  const data = snapshot.exists() ? snapshot.data() : {};
  return {
    userId,
    rosterOrganizationIds: Array.isArray(data.rosterOrganizationIds) ? data.rosterOrganizationIds : [],
    registrationOrganizationIds: Array.isArray(data.registrationOrganizationIds) ? data.registrationOrganizationIds : [],
    regionalRegistrationOrganizationIds: Array.isArray(data.regionalRegistrationOrganizationIds) ? data.regionalRegistrationOrganizationIds : [],
    updatedAt: data.updatedAt,
  };
}

export async function getClubTransferRequests(): Promise<ClubTransferRequest[]> {
  const snap = await getDocs(collection(db, "transferRequests"));
  return snap.docs.map((row) => ({ id: row.id, ...row.data() } as ClubTransferRequest));
}

export async function getAthleteMembershipHistory(): Promise<AthleteMembershipHistory[]> {
  const snap = await getDocs(collection(db, "athleteMemberships"));
  return snap.docs.map((row) => ({ id: row.id, ...row.data() } as AthleteMembershipHistory));
}

/**
 * Reads historical registration club snapshots only for the visible athlete
 * page. A frequent legacy value is a suggestion, never a verified membership.
 */
export async function getHistoricalClubSuggestions(athleteIds: string[]): Promise<MembershipSuggestion[]> {
  const unique = [...new Set(athleteIds.filter(Boolean))];
  const counts = new Map<string, Map<string, number>>();
  for (let index = 0; index < unique.length; index += 30) {
    const chunk = unique.slice(index, index + 30);
    if (!chunk.length) continue;
    const snap = await getDocs(query(collection(db, "registrations"), where("athleteId", "in", chunk)));
    snap.docs.forEach((doc) => {
      const row = doc.data() as any;
      const athleteId = String(row.athleteId || "");
      const club = String(row.club || "").trim();
      if (!athleteId || !club) return;
      const athleteCounts = counts.get(athleteId) || new Map<string, number>();
      athleteCounts.set(club, (athleteCounts.get(club) || 0) + 1);
      counts.set(athleteId, athleteCounts);
    });
  }
  return unique.flatMap((athleteId) => {
    const sorted = [...(counts.get(athleteId)?.entries() || [])].sort((a, b) => b[1] - a[1]);
    return sorted.length
      ? [{ athleteId, suggestedClub: sorted[0][0], occurrenceCount: sorted[0][1], alternatives: sorted.slice(1).map(([name]) => name) }]
      : [];
  });
}

export async function importLegacyClubsAsOrganizations(): Promise<number> {
  const result = await manageOrganization({ action: "import_legacy_clubs" });
  return Number(result.data.imported || 0);
}

export async function bulkAssignAthleteMemberships(
  organizationId: string,
  athleteIds: string[]
): Promise<{ created: number; skipped: number }> {
  const result = await manageOrganization({ action: "bulk_assign_memberships", organizationId, athleteIds });
  return { created: Number(result.data.created || 0), skipped: Number(result.data.skipped || 0) };
}

export async function createOrganization(data: {
  officialName: string;
  shortName?: string;
  type: OrganizationType;
  countryCode: string;
  countryName?: string;
  parentOrganizationId?: string | null;
  regionCode?: string | null;
  location?: string;
  publicDescription?: string;
  imageUrl?: string;
}): Promise<string> {
  const result = await manageOrganization({ action: "create_organization", ...data });
  return String(result.data.organizationId);
}

export async function updateOrganization(data: {
  organizationId: string; officialName: string; shortName?: string; type: OrganizationType;
  countryCode: string; countryName?: string; status: "ACTIVE" | "INACTIVE"; slug?: string;
  location?: string; publicDescription?: string; imageUrl?: string; reason: string;
}): Promise<void> {
  await manageOrganization({ action: "update_organization", ...data });
}

export async function submitAffiliationClaim(data: {
  athleteId: string;
  organizationId: string;
  reason?: string;
}): Promise<string> {
  const result = await manageOrganization({ action: "submit_claim", ...data });
  return String(result.data.claimId);
}

export async function reviewAffiliationClaim(data: {
  claimId: string;
  decision: "APPROVED" | "REJECTED";
  reason: string;
}): Promise<void> {
  await manageOrganization({ action: "review_claim", ...data });
}

export async function requestClubTransfer(data: {
  athleteId: string;
  toOrganizationId: string;
  reason: string;
  evidenceUrl?: string;
}): Promise<string> {
  const result = await manageOrganization({ action: "request_transfer", ...data });
  return String(result.data.transferRequestId);
}

export async function reviewClubTransfer(data: {
  transferRequestId: string;
  decision: "APPROVED" | "REJECTED";
  reason: string;
}): Promise<void> {
  await manageOrganization({ action: "review_transfer", ...data });
}

export async function grantOrganizationPermission(data: {
  userId: string;
  organizationId: string;
  permission: "organization.manage_roster" | "event.register" | "event.register_regional_delegation";
}): Promise<string> {
  const result = await manageOrganization({ action: "grant_permission", ...data });
  return String(result.data.permissionGrantId);
}

export async function revokeOrganizationPermission(permissionGrantId: string, reason: string): Promise<void> {
  await manageOrganization({ action: "revoke_permission", permissionGrantId, reason });
}

export async function setAthleteStatuses(
  athleteDocIds: string[],
  status: "ACTIVE" | "INACTIVE" | "ARCHIVED",
  reason: string
): Promise<number> {
  const result = await manageOrganization({ action: "bulk_set_athlete_status", athleteDocIds, status, reason });
  return Number(result.data.updated || 0);
}

export async function setAthletePublicProfile(
  athleteDocId: string,
  published: boolean,
  reason = ""
): Promise<void> {
  await manageOrganization({ action: "set_public_profile", athleteDocId, published, reason });
}

/** Administrative projection repair only. It never changes source organizations,
 * memberships, users, athletes, registrations, results or rankings. */
export async function rebuildPublicDirectoryProjection(): Promise<{ organizations: number; coaches: number }> {
  const result = await manageOrganization({ action: "rebuild_public_directory" });
  return { organizations: Number(result.data.organizations || 0), coaches: Number(result.data.coaches || 0) };
}
